import { Plate } from '../components/Card';
import React, { useEffect, useState, useCallback } from 'react';
import { getBankBalances, getBankOverdraftCost, getBankTransactions, getBankSummaryByMonth, getBankSummaryByDate, getRetailCash, getFinDirectorCash, postFinDirectorCash } from '../api';

/* ─── Фирменные цвета банков ──────────────────────────────────────────────── */
const BRAND = {
  sber:         { bg: '#21A038', text: '#fff',    border: '#21A038', glow: '#21A03840' },
  tinkoff:      { bg: '#FFDD2D', text: '#1a1a1a', border: '#FFDD2D', glow: '#FFDD2D40' },
  tinkoff_bob:  { bg: '#FFDD2D', text: '#1a1a1a', border: '#FFDD2D', glow: '#FFDD2D40' },
  tochka:        { bg: '#5B3BF5', text: '#fff',    border: '#5B3BF5', glow: '#5B3BF540' },
  tochka_bober:  { bg: '#5B3BF5', text: '#fff',    border: '#5B3BF5', glow: '#5B3BF540' },
  tochka_kozlov: { bg: '#5B3BF5', text: '#fff',    border: '#5B3BF5', glow: '#5B3BF540' },
};

/* ─── SVG-логотипы банков ─────────────────────────────────────────────────── */
const SberLogo = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
    <rect width="40" height="40" rx="10" fill="#21A038"/>
    <polyline points="10,21 17,28 30,13" stroke="white" strokeWidth="4"
      strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

const TinkoffLogo = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
    <rect width="40" height="40" rx="10" fill="#FFDD2D"/>
    <text x="20" y="29" textAnchor="middle" fontSize="26" fontWeight="900"
      fontFamily="'Arial Black',Arial,sans-serif" fill="#111">T</text>
  </svg>
);

const TochkaLogo = ({ size = 32 }) => {
  const r = size / 40;
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
      <rect width="40" height="40" rx="10" fill="#5B3BF5"/>
      <text x="20" y="25" textAnchor="middle" fontSize="12" fontWeight="700"
        fontFamily="Arial,sans-serif" fill="white" letterSpacing="0.5">точка</text>
    </svg>
  );
};

const BankIcon = ({ bank, size = 32 }) => {
  if (bank === 'sber')                              return <SberLogo size={size} />;
  if (bank === 'tinkoff' || bank === 'tinkoff_bob') return <TinkoffLogo size={size} />;
  if (bank === 'tochka' || bank === 'tochka_bober' || bank === 'tochka_kozlov') return <TochkaLogo size={size} />;
  return (
    <div style={{ width: size, height: size, borderRadius: 8, background: 'var(--accent)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.5 }}>🏦</div>
  );
};
/* ─────────────────────────────────────────────────────────────────────────── */

const BANK_META = {
  sber:          { name: 'Сбербанк',                 color: '#0e9f6e' },
  tinkoff:       { name: 'ИП Бобров Илья · Т-Банк', color: '#00a7dc' },
  tinkoff_bob:   { name: 'Т-Банк (ООО БОБ)',         color: '#00a7dc' },
  tochka:         { name: 'ИП Бобров Илья · Точка',   color: '#00a7dc' },
  tochka_bober:   { name: 'ИП Бобров Денис · Точка', color: '#00a7dc' },
  tochka_kozlov:  { name: 'ИП Козлов Иван · Точка',  color: '#00a7dc' },
};

const fmt = n => n != null ? n.toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + ' ₽' : '—';
const fmtKop = n => n != null ? n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₽' : '—';
const fmtStaleDate = at => at ? new Date(at).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

// Банк не отвечает: вместо старой суммы — «нет связи» и дата последних данных.
const StaleValue = ({ snapped_at }) => (
  <>
    <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--text2)', whiteSpace: 'nowrap' }}>нет связи</div>
    <div style={{ fontSize: 11, color: '#e08a00', marginTop: 6, fontWeight: 600 }}>
      ⚠ банк не отвечает · данные от {fmtStaleDate(snapped_at)}
    </div>
  </>
);

const MONTHS_RU = ['Январь','Февраль','Март','Апрель','Май','Июнь',
                   'Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];

/* Возвращает последние N месяцев в формате [{value:'2026-03', label:'Март 2026'}, ...] */
function buildMonthOptions(count = 12) {
  const options = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = `${MONTHS_RU[d.getMonth()]} ${d.getFullYear()}`;
    options.push({ value, label });
  }
  return options;
}

const monthLabel = (monthStr) => {
  if (!monthStr) return '';
  const [year, mon] = monthStr.split('-');
  return `${MONTHS_RU[parseInt(mon, 10) - 1]} ${year}`;
};

function MonthSelector({ selectedMonth, onChange }) {
  const options = buildMonthOptions(24);
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
      <span style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>Месяц:</span>
      <select
        value={selectedMonth}
        onChange={e => onChange(e.target.value)}
        style={{
          background: 'var(--bg2)', color: 'var(--text)',
          border: '1px solid var(--border)', borderRadius: 8,
          padding: '7px 12px', fontSize: 13, cursor: 'pointer',
          outline: 'none', minWidth: 160,
        }}
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}

const getDateByOffset = (offset) => {
  const d = new Date();
  d.setDate(d.getDate() - offset);
  return d.toISOString().slice(0, 10);
};
const formatDayLabel = (offset) => {
  if (offset === 0) return 'сегодня';
  if (offset === 1) return 'вчера';
  if (offset === 2) return 'позавчера';
  const d = new Date();
  d.setDate(d.getDate() - offset);
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
};

function BankCard({ bank, balance, currency, snapped_at, stale, monthIncomeValue }) {
  const meta  = BANK_META[bank] || { name: bank, color: 'var(--accent)' };
  const brand = BRAND[bank]    || { bg: meta.color, text: '#fff', border: meta.color, glow: meta.color + '30' };
  const m = new Date();
  const monthLabel = m.toLocaleDateString('ru-RU', { month: 'long' });
  return (
    <div style={{
      background: 'var(--bg2)',
      borderRadius: 14, padding: '18px 20px',
      border: '1px solid var(--border)',
      boxShadow: 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <BankIcon bank={bank} size={36} />
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{meta.name}</span>
      </div>
      {stale ? <StaleValue snapped_at={snapped_at} /> : (
        <div style={{ fontSize: 26, fontWeight: 800, color: meta.color }}>{fmt(balance)}</div>
      )}
      {!stale && monthIncomeValue > 0 && (
        <div style={{
          marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 5,
          background: `${meta.color}18`, border: `1px solid ${meta.color}40`,
          borderRadius: 8, padding: '4px 10px',
        }}>
          <span style={{ fontSize: 11, color: meta.color, fontWeight: 700 }}>▲</span>
          <span style={{ fontSize: 12, color: meta.color, fontWeight: 700 }}>{fmt(monthIncomeValue)}</span>
          <span style={{ fontSize: 10, color: 'var(--text2)' }}>приход · {monthLabel}</span>
        </div>
      )}
      {!stale && (
        <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 6 }}>
          {currency} · {snapped_at ? new Date(snapped_at).toLocaleString('ru-RU', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' }) : ''}
        </div>
      )}
    </div>
  );
}

export default function Banks() {
  const [balances, setBalances] = useState([]);
  const [txns, setTxns] = useState([]);
  const [summary, setSummary] = useState([]);
  const [prevMonthSummary, setPrevMonthSummary] = useState([]);
  const [bankFilter, setBankFilter] = useState('');
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [days, setDays] = useState(30); // kept for transactions filter
  const nowDate = new Date();
  const currentMonth = `${nowDate.getFullYear()}-${String(nowDate.getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [dirFilter, setDirFilter] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  const [daySummary, setDaySummary] = useState({ income: 0, expense: 0, income_cnt: 0, expense_cnt: 0 });
  const [dayOffset, setDayOffset]   = useState(0); // 0=сегодня, 1=вчера, 2=позавчера...

  const [overdraftCost, setOverdraftCost] = useState(null);

  // Наличка розница и Финдиректор
  const [retailCash,  setRetailCash]  = useState(null);
  const [academyCash, setAcademyCash] = useState(null);
  const [academyModal, setAcademyModal] = useState(null); // 'in' | 'out' | null
  const [academyInput, setAcademyInput] = useState('');
  const [academyBranch, setAcademyBranch] = useState('');
  const [academyDesc,  setAcademyDesc]  = useState('');
  const [academyLoading, setAcademyLoading] = useState(false);

  // Сводка по всем банкам за текущий месяц (для счётчиков на карточках)
  const [cardSummary, setCardSummary] = useState([]);

  const fetchTxns = useCallback(() => {
    const filterBank = tab === 'bob' ? 'tinkoff_bob'
      : tab === 'tinkoff' ? 'tinkoff'
      : (bankFilter || undefined);
    const [year, month] = selectedMonth.split('-').map(Number);
    const prevMonth = month === 1
      ? `${year - 1}-12`
      : `${year}-${String(month - 1).padStart(2, '0')}`;
    getBankTransactions(filterBank, days, search)
      .then(data => { setTxns([...data].sort((a, b) => (b.id || 0) - (a.id || 0))); })
      .catch(() => {});
    getBankSummaryByMonth(selectedMonth, filterBank)
      .then(d => { setSummary(d); })
      .catch(() => {});
    getBankSummaryByMonth(prevMonth, filterBank)
      .then(d => { setPrevMonthSummary(d); })
      .catch(() => { setPrevMonthSummary([]); });
  }, [tab, bankFilter, days, search, selectedMonth]);

  const fetchDaySummary = useCallback(() => {
    const filterBank = tab === 'bob' ? 'tinkoff_bob'
      : tab === 'tinkoff' ? 'tinkoff'
      : (bankFilter || undefined);
    getBankSummaryByDate(getDateByOffset(dayOffset), filterBank)
      .then(data => {
        const inc     = data.reduce((s, x) => s + x.income,      0);
        const exp     = data.reduce((s, x) => s + x.expense,     0);
        const inc_cnt = data.reduce((s, x) => s + x.income_cnt,  0);
        const exp_cnt = data.reduce((s, x) => s + x.expense_cnt, 0);
        setDaySummary({ income: inc, expense: exp, income_cnt: inc_cnt, expense_cnt: exp_cnt });
      })
      .catch(() => {});
  }, [tab, bankFilter, dayOffset]);

  useEffect(() => {
    getBankBalances().then(setBalances).catch(() => {});
    getBankOverdraftCost().then(setOverdraftCost).catch(() => {});
    getRetailCash().then(setRetailCash).catch(() => {});
    getFinDirectorCash().then(setAcademyCash).catch(() => {});
  }, []);

  useEffect(() => {
    // Загружаем сводку по ВСЕМ банкам без фильтра для карточек
    getBankSummaryByMonth(currentMonth).then(setCardSummary).catch(() => {});
  }, [currentMonth]);
  useEffect(() => { fetchTxns(); },       [fetchTxns]);
  useEffect(() => { fetchDaySummary(); }, [fetchDaySummary]);

  // Реальный итог = банки + наличка розницы + касса Финдиректора
  const freshBalances = balances.filter(b => !b.stale);
  const staleBalances = balances.filter(b => b.stale);
  const totalBanks    = freshBalances.reduce((s, b) => s + (b.own_balance !== undefined ? b.own_balance : b.balance), 0);
  const totalRetailCash   = retailCash  ? (retailCash.total   || 0) : 0;
  const totalAcademyCash  = academyCash ? (academyCash.balance || 0) : 0;
  const totalAll = totalBanks + totalRetailCash + totalAcademyCash;
  const bobBalance       = balances.find(b => b.bank === 'tinkoff_bob');
  const ilyaTinkoff      = balances.find(b => b.bank === 'tinkoff');
  const operationalBanks = ['tinkoff_bob', 'tinkoff'];

  // Плата за овердрафт: банк проводит её на следующий день, поэтому «за день» = сегодня, если уже есть, иначе вчера
  const odDay = overdraftCost
    ? ((overdraftCost.today?.total || 0) > 0 ? overdraftCost.today : overdraftCost.yesterday)
    : null;
  const odDayLabel = odDay ? odDay.date.split('-').reverse().slice(0, 2).join('.') : '';
  const odMonthLabel = overdraftCost ? monthLabel(overdraftCost.month.month).split(' ')[0].toLowerCase() : '';
  const odTitle = overdraftCost ? [
    overdraftCost.last_charge ? `Последнее списание: ${overdraftCost.last_charge.date.split('-').reverse().join('.')} — ${fmt(overdraftCost.last_charge.total)}` : 'Списаний нет',
    `${monthLabel(overdraftCost.prev_month.month)}: ${fmt(overdraftCost.prev_month.total)}`,
  ].filter(Boolean).join('\n') : '';

  // Aggregate summary (для таблицы месяца внизу)
  const totalIncome  = summary.reduce((s, x) => s + x.income, 0);
  const totalExpense = summary.reduce((s, x) => s + x.expense, 0);
  const prevTotalIncome = prevMonthSummary.reduce((s, x) => s + x.income, 0);
  const [selYear, selMonth] = selectedMonth.split('-').map(Number);
  const isCurrentSelectedMonth = selYear === nowDate.getFullYear() && selMonth === (nowDate.getMonth() + 1);
  const daysInSelectedMonth = new Date(selYear, selMonth, 0).getDate();
  const daysPassedSelectedMonth = isCurrentSelectedMonth ? nowDate.getDate() : daysInSelectedMonth;
  const projectedMonthIncome = daysPassedSelectedMonth > 0
    ? Math.round((totalIncome / daysPassedSelectedMonth) * daysInSelectedMonth)
    : totalIncome;
  const incomeTrendBase = isCurrentSelectedMonth ? projectedMonthIncome : totalIncome;
  const incomeTrendPct = prevTotalIncome > 0 ? ((incomeTrendBase - prevTotalIncome) / prevTotalIncome) * 100 : null;
  const incomeTrendUp = incomeTrendPct !== null && incomeTrendPct >= 0;

  // Общий приход по всем банкам за текущий месяц (для Собственной кассы)
  const totalMonthIncome = cardSummary.reduce((s, x) => s + x.income, 0);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const displayedTxns = [...txns]
    .filter(t => !dirFilter || t.direction === dirFilter)
    .sort((a, b) => {
      let cmp = 0;
      if (sortField === 'amount') {
        cmp = a.amount - b.amount;
      } else {
        const da = new Date(a.date).getTime() || 0;
        const db = new Date(b.date).getTime() || 0;
        cmp = da !== db ? da - db : (a.id || 0) - (b.id || 0);
      }
      return sortDir === 'desc' ? -cmp : cmp;
    });

  // Приход за текущий месяц по конкретному банку
  const monthIncome = (bank) => {
    const row = cardSummary.find(x => x.bank === bank);
    return row ? row.income : 0;
  };

  // Мини-бейдж: "▲ 1 234 567 ₽ за март"
  const MonthBadge = ({ income, color = '#0e9f6e' }) => {
    if (!income) return null;
    const m = new Date();
    const label = m.toLocaleDateString('ru-RU', { month: 'long' });
    return (
      <div style={{
        marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 5,
        background: `${color}18`, border: `1px solid ${color}40`,
        borderRadius: 8, padding: '4px 10px',
      }}>
        <span style={{ fontSize: 11, color, fontWeight: 700 }}>▲</span>
        <span style={{ fontSize: 12, color, fontWeight: 700 }}>{fmt(income)}</span>
        <span style={{ fontSize: 10, color: 'var(--text2)' }}>приход · {label}</span>
      </div>
    );
  };

  const handleAcademySubmit = async () => {
    const amt = parseFloat(academyInput.replace(/\s/g, '').replace(',', '.'));
    if (!amt || amt <= 0) return;
    setAcademyLoading(true);
    try {
      const result = await postFinDirectorCash(amt, academyModal, academyDesc, academyBranch);
      setAcademyCash(prev => ({ ...prev, balance: result.balance, updated_at: result.updated_at }));
      setAcademyModal(null);
      setAcademyInput('');
      setAcademyBranch('');
      setAcademyDesc('');
      getFinDirectorCash().then(setAcademyCash).catch(() => {});
    } catch (e) {
      alert('Ошибка при сохранении');
    } finally {
      setAcademyLoading(false);
    }
  };

  return (
    <div>
      <div className="kp-hero">
        <Plate>Финансы · банки</Plate>
        <h1 style={{ margin: 0 }}>Банки</h1>
        <p style={{ color: 'var(--text2)', margin: '8px 0 0', fontSize: 13 }}>
          Остатки и транзакции по всем счетам
        </p>
      </div>

      {/* Total + BOB summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12, marginBottom: 24 }}>
        <div style={{ background: 'var(--bg2)', borderRadius: 14, padding: '16px 18px', border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6, textTransform: 'uppercase' }}>Собственная касса</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: totalAll < 0 ? 'var(--red)' : '#0e9f6e' }}>{fmt(totalAll)}</div>
          {totalMonthIncome > 0 && (
            <div style={{
              marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 5,
              background: '#0e9f6e18', border: '1px solid #0e9f6e40',
              borderRadius: 8, padding: '4px 10px',
            }}>
              <span style={{ fontSize: 11, color: '#0e9f6e', fontWeight: 700 }}>▲</span>
              <span style={{ fontSize: 12, color: '#0e9f6e', fontWeight: 700 }}>{fmt(totalMonthIncome)}</span>
              <span style={{ fontSize: 10, color: 'var(--text2)' }}>приход · {new Date().toLocaleDateString('ru-RU', { month: 'long' })}</span>
            </div>
          )}
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <div style={{ fontSize: 11, color: 'var(--text2)', display: 'flex', justifyContent: 'space-between' }}>
              <span>🏦 Банки</span>
              <span style={{ color: 'var(--text1)', fontWeight: 600 }}>{fmt(totalBanks)}</span>
            </div>
            {totalRetailCash > 0 && (
              <div style={{ fontSize: 11, color: 'var(--text2)', display: 'flex', justifyContent: 'space-between' }}>
                <span>💵 Наличка розница</span>
                <span style={{ color: '#0e9f6e', fontWeight: 600 }}>{fmt(totalRetailCash)}</span>
              </div>
            )}
            {totalAcademyCash > 0 && (
              <div style={{ fontSize: 11, color: 'var(--text2)', display: 'flex', justifyContent: 'space-between' }}>
                <span>📈 Касса Финдиректора</span>
                <span style={{ color: '#00a7dc', fontWeight: 600 }}>{fmt(totalAcademyCash)}</span>
              </div>
            )}
          </div>
          {staleBalances.length > 0 && (
            <div style={{ fontSize: 11, color: '#e08a00', marginTop: 6, fontWeight: 600 }}>
              ⚠ Не учтены (нет связи): {staleBalances.map(b => BANK_META[b.bank]?.name || b.label || b.bank).join(', ')}
            </div>
          )}
          <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 6, borderTop: '1px solid var(--border)', paddingTop: 5 }}>
            {freshBalances.length} счетов · банки + наличка
          </div>
        </div>

        {bobBalance && (
          <div style={{
            background: 'var(--bg2)',
            borderRadius: 14, padding: '16px 18px',
            border: '1px solid var(--border)',
            boxShadow: 'none',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <TinkoffLogo size={36} />
              <div>
                <div style={{ fontSize: 11, color: 'var(--accent)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>Т-Банк</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>ООО БОБ</div>
              </div>
            </div>
            {bobBalance.stale ? <StaleValue snapped_at={bobBalance.snapped_at} /> : (
              <div style={{ fontSize: 26, fontWeight: 800, color: '#00a7dc' }}>{fmt(bobBalance.balance)}</div>
            )}
            {!bobBalance.stale && <MonthBadge income={monthIncome('tinkoff_bob')} color="#00a7dc" />}
            <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 6 }}>Роялти ФДМ / КПК</div>
          </div>
        )}

        {ilyaTinkoff && (
          <div style={{
            background: 'var(--bg2)',
            borderRadius: 14, padding: '16px 18px',
            border: '1px solid var(--border)',
            boxShadow: 'none',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <TinkoffLogo size={36} />
              <div>
                <div style={{ fontSize: 11, color: 'var(--accent)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>Т-Банк</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>ИП Бобров Илья</div>
              </div>
            </div>
            {ilyaTinkoff.stale ? <StaleValue snapped_at={ilyaTinkoff.snapped_at} /> : (
              <div style={{ fontSize: 26, fontWeight: 800, color: (ilyaTinkoff.own_balance ?? ilyaTinkoff.balance) < 0 ? '#e04848' : '#00a7dc' }}>
                {fmt(ilyaTinkoff.own_balance !== undefined ? ilyaTinkoff.own_balance : ilyaTinkoff.balance)}
              </div>
            )}
            {ilyaTinkoff.stale && ilyaTinkoff.overdraft > 0 && (
              <div style={{ fontSize: 10, color: 'var(--text2)', display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                <span>Лимит овердрафта</span>
                <span>{fmt(ilyaTinkoff.overdraft)}</span>
              </div>
            )}
            {!ilyaTinkoff.stale && ilyaTinkoff.overdraft > 0 && (
              <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <div style={{ fontSize: 11, color: 'var(--text2)', display: 'flex', justifyContent: 'space-between', whiteSpace: 'nowrap', gap: 8 }}>
                  <span>Доступно с овердрафтом</span>
                  <span style={{ color: 'var(--text1)', fontWeight: 600 }}>{fmtKop(ilyaTinkoff.balance)}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text2)', display: 'flex', justifyContent: 'space-between', whiteSpace: 'nowrap', gap: 8 }}>
                  <span>Долг по овердрафту</span>
                  <span style={{ color: '#e04848', fontWeight: 600 }}>
                    {ilyaTinkoff.overdraft > ilyaTinkoff.balance ? `−${fmtKop(ilyaTinkoff.overdraft - ilyaTinkoff.balance)}` : fmtKop(0)}
                  </span>
                </div>
                <div style={{ fontSize: 10, color: 'var(--text2)', display: 'flex', justifyContent: 'space-between', whiteSpace: 'nowrap', gap: 8, marginTop: 1 }}>
                  <span>Лимит</span>
                  <span style={{ color: 'var(--text2)' }}>{fmt(ilyaTinkoff.overdraft)}</span>
                </div>
              </div>
            )}
            {overdraftCost && !ilyaTinkoff.stale && (
              <div title={odTitle} style={{ marginTop: 6, paddingTop: 5, borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 3 }}>
                <div style={{ fontSize: 11, color: 'var(--text2)', fontWeight: 600 }}>Плата за овердрафт</div>
                <div style={{ fontSize: 11, color: 'var(--text2)', display: 'flex', justifyContent: 'space-between', whiteSpace: 'nowrap' }}>
                  <span>за день · {odDayLabel}</span>
                  <span style={{ color: '#e04848', fontWeight: 600 }}>{fmt(odDay.total)}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text2)', display: 'flex', justifyContent: 'space-between', whiteSpace: 'nowrap' }}>
                  <span>за {odMonthLabel}</span>
                  <span style={{ color: '#e04848', fontWeight: 600 }}>{fmt(overdraftCost.month.total)}</span>
                </div>
              </div>
            )}
            {!ilyaTinkoff.stale && <MonthBadge income={monthIncome('tinkoff')} color="#00a7dc" />}
          </div>
        )}

        {/* Наличка Розница — собственные точки ИП Бобров Илья */}
        <div style={{
          background: 'var(--bg2)',
          borderRadius: 14, padding: '16px 18px',
          border: '1px solid var(--border)',
          boxShadow: 'none',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(14,159,110,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>💵</div>
            <div>
              <div style={{ fontSize: 11, color: '#0e9f6e', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>Наличка</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Розница · ИП Бобров Илья</div>
            </div>
          </div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#0e9f6e' }}>
            {retailCash ? fmt(retailCash.total) : '—'}
          </div>
          {retailCash && retailCash.stores.length > 0 && (
            <div style={{ marginTop: 8, maxHeight: 80, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
              {retailCash.stores.map((s, i) => (
                <div key={i} style={{ fontSize: 11, color: 'var(--text2)', display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
                  <span>{s.name}</span>
                  <span style={{ color: 'var(--text1)', fontWeight: 600 }}>{fmt(s.balance)}</span>
                </div>
              ))}
            </div>
          )}
          <div style={{ fontSize: 10, color: 'var(--text2)', marginTop: 6 }}>
            Пробитые наличные · МойСклад
          </div>
        </div>

        {balances.filter(b => !operationalBanks.includes(b.bank)).map(b => (
          <BankCard key={b.bank} {...b} monthIncomeValue={monthIncome(b.bank)} />
        ))}

        {/* Наличка Финдиректор — стиль как Розница */}
        <div style={{
          background: 'var(--bg2)',
          borderRadius: 14, padding: '16px 18px',
          border: '1px solid var(--border)',
          boxShadow: 'none',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(0,167,220,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>📈</div>
            <div>
              <div style={{ fontSize: 11, color: '#00a7dc', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>Наличка</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Касса Финдиректора</div>
            </div>
          </div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#00a7dc' }}>
            {academyCash ? fmt(academyCash.balance) : '0 ₽'}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button onClick={() => { setAcademyModal('in'); setAcademyInput(''); setAcademyBranch(''); setAcademyDesc(''); }} style={{
              flex: 1, padding: '5px 0', borderRadius: 6, border: 'none', cursor: 'pointer',
              background: 'rgba(14,159,110,0.2)', color: '#0e9f6e', fontWeight: 700, fontSize: 11,
            }}>+ Внести</button>
            <button onClick={() => { setAcademyModal('out'); setAcademyInput(''); setAcademyBranch(''); setAcademyDesc(''); }} style={{
              flex: 1, padding: '5px 0', borderRadius: 6, border: 'none', cursor: 'pointer',
              background: 'rgba(224,72,72,0.15)', color: '#e04848', fontWeight: 700, fontSize: 11,
            }}>− Выплатить</button>
          </div>
          {academyCash && academyCash.history && academyCash.history.length > 0 && (
            <div style={{ marginTop: 8, maxHeight: 80, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
              {academyCash.history.map((h, i) => {
                const dt = h.date ? new Date(h.date) : null;
                const dateStr = dt ? dt.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }) : '';
                return (
                  <div key={h.id} style={{ fontSize: 11, color: 'var(--text2)', display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
                    <span>{h.description || (h.direction === 'in' ? 'Пополнение' : 'Выплата')} · {dateStr}</span>
                    <span style={{ color: h.direction === 'in' ? '#0e9f6e' : '#e04848', fontWeight: 600 }}>
                      {h.direction === 'in' ? '+' : '−'}{fmt(h.amount)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ fontSize: 10, color: 'var(--text2)', marginTop: 6 }}>Ручной учёт</div>
        </div>
      </div>

      {/* Модальное окно для операций с кассой Финдиректора */}
      {academyModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={e => { if (e.target === e.currentTarget) setAcademyModal(null); }}>
          <div style={{
            background: 'var(--bg2)', borderRadius: 16, padding: '28px 32px', width: 360,
            border: academyModal === 'in' ? '2px solid rgba(14,159,110,0.5)' : '2px solid rgba(224,72,72,0.5)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text)', marginBottom: 20 }}>
              {academyModal === 'in' ? '💵 Внести наличные' : '💸 Выплатить из кассы'}
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase' }}>Сумма, ₽</div>
              <input
                autoFocus
                type="text"
                inputMode="numeric"
                value={academyInput}
                onChange={e => setAcademyInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAcademySubmit()}
                placeholder="0"
                style={{
                  width: '100%', boxSizing: 'border-box',
                  background: 'var(--bg3)', color: 'var(--text)',
                  border: '1px solid var(--border)', borderRadius: 10,
                  padding: '12px 14px', fontSize: 20, fontWeight: 700, outline: 'none',
                }}
              />
            </div>
            {academyModal === 'out' && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase' }}>Филиал</div>
                <input
                  type="text"
                  value={academyBranch}
                  onChange={e => setAcademyBranch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAcademySubmit()}
                  placeholder="Например: Сандеро Мартынов"
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    background: 'var(--bg3)', color: 'var(--text)',
                    border: '1px solid var(--border)', borderRadius: 10,
                    padding: '10px 14px', fontSize: 14, outline: 'none',
                  }}
                />
              </div>
            )}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase' }}>Комментарий (необязательно)</div>
              <input
                type="text"
                value={academyDesc}
                onChange={e => setAcademyDesc(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAcademySubmit()}
                placeholder={academyModal === 'in' ? 'Поступление Финдиректора...' : 'Зарплата, расходы...'}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  background: 'var(--bg3)', color: 'var(--text)',
                  border: '1px solid var(--border)', borderRadius: 10,
                  padding: '10px 14px', fontSize: 14, outline: 'none',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setAcademyModal(null)} style={{
                flex: 1, padding: '12px 0', borderRadius: 10, border: '1px solid var(--border)',
                background: 'transparent', color: 'var(--text2)', cursor: 'pointer', fontSize: 14, fontWeight: 600,
              }}>Отмена</button>
              <button onClick={handleAcademySubmit} disabled={academyLoading} style={{
                flex: 2, padding: '12px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
                background: academyModal === 'in' ? '#0e9f6e' : '#e04848',
                color: '#fff', fontSize: 15, fontWeight: 800,
                opacity: academyLoading ? 0.6 : 1,
              }}>
                {academyLoading ? 'Сохранение...' : academyModal === 'in' ? 'Внести' : 'Выплатить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Дневная статистика с навигацией */}
      <div style={{ marginBottom: 16 }}>
        {/* Навигатор дней */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <button
            onClick={() => setDayOffset(o => o + 1)}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text1)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
          >←</button>
          <div style={{ flex: 1, textAlign: 'center', fontSize: 14, fontWeight: 700, color: 'var(--text1)' }}>
            {dayOffset === 0 ? '📅 Сегодня' : dayOffset === 1 ? '📅 Вчера' : dayOffset === 2 ? '📅 Позавчера' : `📅 ${getDateByOffset(dayOffset).split('-').reverse().join('.')}`}
          </div>
          <button
            onClick={() => setDayOffset(o => Math.max(0, o - 1))}
            disabled={dayOffset === 0}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg2)', color: dayOffset === 0 ? 'var(--text2)' : 'var(--text1)', cursor: dayOffset === 0 ? 'default' : 'pointer', fontSize: 16, lineHeight: 1, opacity: dayOffset === 0 ? 0.4 : 1 }}
          >→</button>
          {dayOffset > 0 && (
            <button
              onClick={() => setDayOffset(0)}
              style={{ padding: '5px 10px', borderRadius: 8, border: '1px solid var(--accent)', background: 'transparent', color: 'var(--accent)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
            >Сегодня</button>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ background: 'rgba(14,159,110,0.1)', borderRadius: 14, padding: '18px 20px', border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 11, color: '#0e9f6e', marginBottom: 4, textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.6 }}>
              💚 Заработали {formatDayLabel(dayOffset)}
            </div>
            <div style={{ fontSize: 30, fontWeight: 900, color: '#0e9f6e' }}>{fmt(daySummary.income)}</div>
            {daySummary.income_cnt > 0 && (
              <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4 }}>{daySummary.income_cnt} транзакций</div>
            )}
          </div>
          <div style={{ background: 'rgba(224,72,72,0.08)', borderRadius: 14, padding: '18px 20px', border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 11, color: '#e04848', marginBottom: 4, textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.6 }}>
              🔴 Потратили {formatDayLabel(dayOffset)}
            </div>
            <div style={{ fontSize: 30, fontWeight: 900, color: '#e04848' }}>{fmt(daySummary.expense)}</div>
            {daySummary.expense_cnt > 0 && (
              <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4 }}>{daySummary.expense_cnt} транзакций</div>
            )}
          </div>
        </div>
      </div>

      {/* Period stats */}
      <>
        {/* Month selector */}
        <MonthSelector selectedMonth={selectedMonth} onChange={setSelectedMonth} />

        {(totalIncome > 0 || totalExpense > 0) && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
            <div style={{ background: 'rgba(14,159,110,0.08)', borderRadius: 12, padding: '14px 16px', border: '1px solid rgba(14,159,110,0.2)' }}>
              <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 4, textTransform: 'uppercase', fontWeight: 700 }}>
                💚 Приход · {monthLabel(selectedMonth)}
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#0e9f6e' }}>{fmt(totalIncome)}</div>
              <div style={{ fontSize: 11, marginTop: 4, color: isCurrentSelectedMonth ? '#0e9f6e' : 'var(--text2)', fontWeight: isCurrentSelectedMonth ? 700 : 500 }}>
                {isCurrentSelectedMonth ? `Прогноз месяца: ${fmt(projectedMonthIncome)}` : `Факт за месяц: ${fmt(totalIncome)}`}
              </div>
              <div style={{ fontSize: 11, marginTop: 2, color: 'var(--text2)' }}>
                Пред. месяц: {fmt(prevTotalIncome)}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 2, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 11, color: 'var(--text2)' }}>{summary.reduce((s, x) => s + x.income_cnt, 0)} транзакций</div>
                {incomeTrendPct !== null && (
                  <div style={{ fontSize: 11, fontWeight: 700, color: incomeTrendUp ? '#0e9f6e' : '#e04848' }}>
                    {incomeTrendUp ? '↑' : '↓'} {Math.abs(incomeTrendPct).toFixed(1)}% {isCurrentSelectedMonth ? 'прогноз к пред. месяцу' : 'к пред. месяцу'}
                  </div>
                )}
              </div>
            </div>
            <div style={{ background: 'rgba(224,72,72,0.08)', borderRadius: 12, padding: '14px 16px', border: '1px solid rgba(224,72,72,0.2)' }}>
              <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 4, textTransform: 'uppercase', fontWeight: 700 }}>
                🔴 Расход · {monthLabel(selectedMonth)}
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#e04848' }}>{fmt(totalExpense)}</div>
              <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>{summary.reduce((s, x) => s + x.expense_cnt, 0)} транзакций</div>
            </div>
            <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: '14px 16px', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 4, textTransform: 'uppercase', fontWeight: 700 }}>
                ⚖️ Чистый поток · {monthLabel(selectedMonth)}
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: totalIncome - totalExpense >= 0 ? '#0e9f6e' : '#e04848' }}>
                {totalIncome - totalExpense >= 0 ? '+' : ''}{fmt(totalIncome - totalExpense)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>
                {summary.reduce((s, x) => s + x.income_cnt + x.expense_cnt, 0)} транзакций
              </div>
            </div>
          </div>
        )}
      </>

      {/* Tabs + filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        {[
          { id: 'all',     label: '🏦 Все' },
          { id: 'bob',     label: '🔵 ООО БОБ' },
          { id: 'tinkoff', label: '🟡 ИП Бобров Илья' },
        ].map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); setBankFilter(''); }} style={{
            padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontWeight: tab === t.id ? 700 : 500, fontSize: 13,
            background: tab === t.id
              ? (t.id === 'tinkoff' ? '#e08a00' : 'var(--accent)')
              : 'var(--bg2)',
            color: tab === t.id ? '#fff' : 'var(--text1)',
          }}>{t.label}</button>
        ))}

        {tab === 'all' && (
          <select value={bankFilter} onChange={e => setBankFilter(e.target.value)}
            style={{ background: 'var(--bg2)', color: 'var(--text)', border: '1px solid var(--border)', padding: '7px 12px', borderRadius: 8, fontSize: 13 }}>
            <option value="">Все банки</option>
            <option value="tinkoff">ИП Бобров Илья · Т-Банк</option>
            <option value="tinkoff_bob">Т-Банк (ООО БОБ)</option>
            <option value="tochka">ИП Бобров Илья · Точка</option>
            <option value="tochka_bober">ИП Бобров Денис · Точка</option>
            <option value="tochka_kozlov">ИП Козлов Иван · Точка</option>
            <option value="sber">Сбербанк</option>
          </select>
        )}

        <select value={days} onChange={e => setDays(+e.target.value)}
          style={{ background: 'var(--bg2)', color: 'var(--text)', border: '1px solid var(--border)', padding: '7px 12px', borderRadius: 8, fontSize: 13 }}>
          <option value={7}>7 дней</option>
          <option value={30}>30 дней</option>
          <option value={60}>60 дней</option>
          <option value={90}>90 дней</option>
        </select>

        {/* Direction filter */}
        <div style={{ display: 'flex', gap: 4 }}>
          {[
            { id: '',    label: '↕ Все' },
            { id: 'in',  label: '↓ Приход' },
            { id: 'out', label: '↑ Расход' },
          ].map(d => (
            <button key={d.id} onClick={() => setDirFilter(d.id)} style={{
              padding: '7px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
              fontWeight: dirFilter === d.id ? 700 : 500, fontSize: 12,
              background: dirFilter === d.id
                ? (d.id === 'in' ? '#0e9f6e' : d.id === 'out' ? '#e04848' : 'var(--accent)')
                : 'var(--bg2)',
              color: dirFilter === d.id ? '#fff' : 'var(--text2)',
            }}>{d.label}</button>
          ))}
        </div>

        {/* Search */}
        <input
          placeholder="🔍 Поиск по контрагенту / описанию…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            marginLeft: 'auto',
            background: 'var(--bg2)', color: 'var(--text1)',
            border: '1px solid var(--border)', padding: '7px 12px',
            borderRadius: 8, fontSize: 13, width: 260, outline: 'none',
          }}
        />
      </div>

      {/* Transactions */}
      <div style={{ background: 'var(--bg2)', borderRadius: 14, border: '1px solid var(--border)', overflow: 'hidden' }}>
        {tab === 'bob' && (
          <div style={{ padding: '12px 16px', background: '#00a7dc15', borderBottom: '1px solid #00a7dc33', fontSize: 13, color: '#00a7dc', fontWeight: 600 }}>
            🔵 ООО БОБ (Т-Банк) — роялти 5% + маркетинг 5% от партнёров ФДМ и КПК Россия
          </div>
        )}
        {tab === 'tinkoff' && (
          <div style={{ padding: '12px 16px', background: '#e08a0015', borderBottom: '1px solid #e08a0033', fontSize: 13, color: '#e08a00', fontWeight: 600 }}>
            🟡 ИП Бобров Илья · Т-Банк
          </div>
        )}

        <div style={{ padding: '10px 14px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: 'var(--text2)' }}>
          <span>Показано транзакций: <b style={{ color: 'var(--text1)' }}>{displayedTxns.length}</b>{dirFilter && <span style={{ marginLeft: 6, color: dirFilter === 'in' ? '#0e9f6e' : '#e04848' }}>({dirFilter === 'in' ? 'только приход' : 'только расход'})</span>}</span>
          {search && <span>Фильтр: «{search}»</span>}
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {[
                  { key: 'date',   label: 'Дата / Время', sortable: true },
                  { key: 'bank',   label: 'Банк',         sortable: false },
                  { key: 'cparty', label: 'Контрагент',   sortable: false },
                  { key: 'desc',   label: 'Описание',     sortable: false },
                  { key: 'amount', label: 'Сумма',        sortable: true },
                ].map(h => (
                  <th key={h.key}
                    onClick={h.sortable ? () => handleSort(h.key) : undefined}
                    style={{
                      padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600,
                      textTransform: 'uppercase', letterSpacing: 0.5,
                      color: sortField === h.key ? 'var(--accent)' : 'var(--text2)',
                      cursor: h.sortable ? 'pointer' : 'default',
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                    }}>
                    {h.label}
                    {h.sortable && (
                      <span style={{ marginLeft: 4, opacity: sortField === h.key ? 1 : 0.3 }}>
                        {sortField === h.key ? (sortDir === 'desc' ? ' ▼' : ' ▲') : ' ↕'}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayedTxns.map((t, i) => {
                const meta = BANK_META[t.bank] || { name: t.bank, color: 'var(--text2)' };
                const dt = t.date ? new Date(t.date) : null;
                const dateStr = dt ? dt.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
                const timeStr = dt ? dt.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '';
                return (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(30,36,37,0.04)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      <div style={{ color: 'var(--text1)', fontSize: 12, fontWeight: 600 }}>{dateStr}</div>
                      <div style={{ color: 'var(--text2)', fontSize: 11, marginTop: 1 }}>{timeStr}</div>
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <BankIcon bank={t.bank} size={20} />
                        <span style={{ fontSize: 11, fontWeight: 600, color: meta.color }}>
                          {meta.name}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '10px 14px', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.counterparty || '—'}
                    </td>
                    <td style={{ padding: '10px 14px', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text2)' }}>
                      {t.description}
                      {t.is_internal && (
                        <span style={{
                          marginLeft: 6, fontSize: 10, fontWeight: 700, color: 'var(--text2)',
                          background: 'rgba(148,163,184,0.15)', border: '1px solid rgba(148,163,184,0.3)',
                          borderRadius: 6, padding: '1px 6px', whiteSpace: 'nowrap',
                        }}>между счетами</span>
                      )}
                    </td>
                    <td style={{ padding: '10px 14px', fontWeight: 700, color: t.is_internal ? 'var(--text2)' : (t.direction === 'in' ? '#0e9f6e' : '#e04848'), whiteSpace: 'nowrap' }}>
                      {t.direction === 'in' ? '+' : '−'}{fmt(t.amount)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!displayedTxns.length && (
            <div style={{ padding: 30, color: 'var(--text2)', textAlign: 'center' }}>
              {search ? `Нет транзакций по запросу «${search}»` : dirFilter ? 'Нет транзакций по выбранному фильтру' : 'Нет транзакций — данные обновятся при следующей синхронизации'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
